import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Row,
  Col,
  ListGroup,
  Image,
  Card,
  Form,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import CheckoutSteps from '../Components/CheckoutSteps';
import Message from '../Components/Message';
import Loader from '../Components/Loader';
import PriceWithSale from '../Components/PriceWithSale';
import { getEffectivePrice } from '../utils/salePricing';
import {
  getLocalDeliveryRate,
  isLocalDeliveryAddress,
  LOCAL_DELIVERY_RATE_ID,
  LOCAL_DELIVERY_THRESHOLD,
  DEFAULT_PICKUP_LOCATION,
} from '../utils/shippingPolicy';
import { fmt } from '../utils/cartUtils';

import {
  useCreateOrderMutation,
  useCreateGuestOrderMutation,
  useQuoteCartRatesMutation,
  useGetPayPalClientIdQuery,
} from '../slices/ordersApiSlice';
import { usePriceCheckoutMutation } from '../slices/checkoutApiSlice';
import { clearCartItems } from '../slices/cartSlice';
import { PayPalButtons } from '@paypal/react-paypal-js';
import BackButton from "../Components/BackButton";
import { buildProductUrl, getCurrentRoute } from '../utils/navigation';

const RATES_TOAST_ID = 'rates-error';
const showRatesErrorOnce = (msg) => {
  if (!toast.isActive(RATES_TOAST_ID)) {
    toast.error(msg || 'Failed to get rates', { toastId: RATES_TOAST_ID });
  }
};
const clearRatesErrorToast = () => {
  if (toast.isActive(RATES_TOAST_ID)) toast.dismiss(RATES_TOAST_ID);
};

const PlaceOrderScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { auth = {}, cart = {} } = useSelector((s) => s);
  const userInfo = auth?.userInfo || null;
  const isTieredUser = Boolean(userInfo?.tier?.id);
  const hasFreeDelivery =
    Boolean(userInfo?.freeDelivery) ||
    Boolean(userInfo?.isFreeDelivery) ||
    Boolean(userInfo?.freeShipping) ||
    Boolean(userInfo?.hasFreeDelivery);
  const tierName = userInfo?.tier?.name || 'Tier';

  const {
    cartItems = [],
    shippingAddress = {},
    paymentMethod,
    guestMode = !userInfo,
    guestInfo = {},
    fulfillmentMethod = 'ship',
    pickupLocation,
  } = cart;

  const [createOrder, createState] = useCreateOrderMutation();
  const [createGuestOrder, createGuestState] =
    useCreateGuestOrderMutation();
  const [priceCheckout, { isLoading: loadingPricing }] = usePriceCheckoutMutation();

  // --- Live rates hook/state ---
  const [quoteCartRates, { isLoading: quotingRates }] =
    useQuoteCartRatesMutation();
  const { data: paypalConfig } = useGetPayPalClientIdQuery();

  // IMPORTANT NEW STATE:
  // shipmentId is what EasyPost (or your shipping service) returns.
  const [shipmentId, setShipmentId] = useState(null);

  // full list of rate objects for this shipment
  const [rates, setRates] = useState([]);
  const [ratesError, setRatesError] = useState('');

  // which rate the user picked (by id)
  const [rateId, setRateId] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [pricingError, setPricingError] = useState('');

  const LOCAL_DELIVERY_CUTOFF = '4:30 PM';

  const pricingLineItemMap = useMemo(() => {
    const map = new Map();
    (pricingData?.lineItems || []).forEach((item) => {
      map.set(String(item.productId), item);
    });
    return map;
  }, [pricingData]);

  const isTierPricing = useMemo(
    () => isTieredUser && cartItems.some((item) => item?.tierPriceApplied),
    [cartItems, isTieredUser]
  );

  const buildOrderBody = (payload) =>
    guestMode ? { ...payload, guest: guestInfo || {} } : payload;

  const submitOrder = async (payload) => {
    const res = guestMode
      ? await createGuestOrder(buildOrderBody(payload)).unwrap()
      : await createOrder(payload).unwrap();

    if (!res?._id) {
      throw new Error('Order id was not returned by the server.');
    }

    dispatch(clearCartItems());
    navigate(guestMode ? `/guest-order-success/${res._id}` : `/order/${res._id}`);
    return res;
  };

  const formatError = (err, fallback) =>
    err?.data?.message ||
    createGuestState?.error?.data?.message ||
    err?.error ||
    err?.message ||
    fallback;

  const itemsSubtotal = useMemo(() => {
    const pricingSubtotal = pricingData?.subtotal;
    const serverItemsTotal = Number.isFinite(Number(pricingSubtotal))
      ? Number(pricingSubtotal)
      : null;
    return Number(
      serverItemsTotal !== null
        ? serverItemsTotal
        : cartItems.reduce(
            (acc, it) =>
              acc +
              Number(it?.price ?? 0) *
                Number(it?.qty ?? 0),
            0
          )
    );
  }, [cartItems, pricingData]);

  const orderLineCount = cartItems.length;
  const totalUnits = useMemo(
    () =>
      cartItems.reduce(
        (acc, it) => acc + Number(it?.qty ?? 0),
        0
      ),
    [cartItems]
  );

  const summaryDestination = useMemo(() => {
    if (fulfillmentMethod === 'pickup') {
      return (
        pickupLocation ||
        DEFAULT_PICKUP_LOCATION
      );
    }
    if (!shippingAddress?.address) return '—';
    const region =
      shippingAddress.province || shippingAddress.state;
    return `${shippingAddress.address}, ${shippingAddress.city}${
      region ? `, ${region}` : ''
    } ${shippingAddress.postalCode}, ${shippingAddress.country}`;
  }, [fulfillmentMethod, pickupLocation, shippingAddress]);

  const isLocalDelivery = useMemo(
    () =>
      fulfillmentMethod === 'ship' &&
      isLocalDeliveryAddress(shippingAddress),
    [fulfillmentMethod, shippingAddress]
  );
  const localDeliveryQualified =
    isLocalDelivery &&
    itemsSubtotal >= LOCAL_DELIVERY_THRESHOLD;
  const localDeliveryRemaining = Math.max(
    0,
    LOCAL_DELIVERY_THRESHOLD - itemsSubtotal
  );

  const localDeliveryRate = useMemo(
    () => ({
      id: LOCAL_DELIVERY_RATE_ID,
      carrier: 'Local Delivery',
      service: localDeliveryQualified
        ? 'Free same-day delivery (orders over $60)'
        : 'Local delivery (Ottawa–Gatineau Area)',
      rate: getLocalDeliveryRate(itemsSubtotal),
      currency: 'CAD',
      delivery_days: null,
    }),
    [itemsSubtotal, localDeliveryQualified]
  );

  const displayRates = useMemo(() => {
    if (fulfillmentMethod !== 'ship') return [];
    if (isLocalDelivery) return [localDeliveryRate];
    return rates;
  }, [fulfillmentMethod, isLocalDelivery, localDeliveryRate, rates]);

  const selectedRate =
    displayRates.find((r) => r.id === rateId) || null;

  const formatRateOption = (rate) => {
    const base = `${rate.carrier} — ${rate.service}`;
    const deliveryDays =
      typeof rate.delivery_days === 'number' ? ` · ${rate.delivery_days}d` : '';

    if (hasFreeDelivery && fulfillmentMethod === 'ship') {
      return `${base} — Free for your account${deliveryDays}`;
    }

    return `${base} — $${Number(rate.rate).toFixed(2)} ${rate.currency}${deliveryDays}`;
  };

  // ---------- totals ----------
  const totals = useMemo(() => {
    // effective items price (uses item.price = sale price)
    const items = Number(itemsSubtotal);

    // original (non-discounted) items price, using originalPrice when present
    const originalItems = Number(
      cartItems.reduce(
        (acc, it) =>
          acc +
          Number(it?.originalPrice ?? it?.price ?? 0) *
            Number(it?.qty ?? 0),
        0
      )
    );

    const discount = Math.max(0, originalItems - items);

    const ship =
      fulfillmentMethod === 'pickup' || hasFreeDelivery
        ? 0
        : Number(selectedRate?.rate ?? 0);

    // you already sometimes store cart.taxPrice, fallback to 13%
    const tax = Number(cart.taxPrice ?? items * 0.13);

    const total = items + ship + tax;

    return {
      itemsPrice: fmt(items),
      originalItemsPrice: fmt(originalItems),
      discount: fmt(discount),
      shippingPrice: fmt(ship),
      taxPrice: fmt(tax),
      totalPrice: fmt(total),
      totalRaw: total,
    };
  }, [
    cartItems,
    fulfillmentMethod,
    itemsSubtotal,
    selectedRate,
    hasFreeDelivery,
    cart.taxPrice,
    pricingData,
  ]);

  useEffect(() => {
    if (fulfillmentMethod !== 'ship') return;

    if (isLocalDelivery && rateId !== LOCAL_DELIVERY_RATE_ID) {
      setRateId(LOCAL_DELIVERY_RATE_ID);
      return;
    }

    if (!isLocalDelivery && rateId === LOCAL_DELIVERY_RATE_ID) {
      setRateId(rates[0]?.id || null);
    }
  }, [fulfillmentMethod, isLocalDelivery, rateId, rates]);

  // ---------- guard redirects ----------
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }

    const needsAddress = fulfillmentMethod === 'ship';
    const hasAddress =
      shippingAddress.address &&
      shippingAddress.city &&
      (shippingAddress.province ||
        shippingAddress.state) &&
      shippingAddress.postalCode &&
      shippingAddress.country;

    if (needsAddress && !hasAddress) {
      navigate(
        guestMode ? '/guest-shipping' : '/shipping',
        { replace: true }
      );
      return;
    }

    if (!paymentMethod) {
      navigate(
        guestMode ? '/guest-payment' : '/payment',
        { replace: true }
      );
    }
  }, [
    shippingAddress,
    paymentMethod,
    fulfillmentMethod,
    guestMode,
    navigate,
  ]);

  // -------------------- Live rate quoting ---------------------
  const quoteInFlightRef = useRef(false);
  const lastQuoteKeyRef = useRef('');

  useEffect(() => {
    if (fulfillmentMethod !== 'ship') {
      setRates([]);
      setRateId(null);
      setShipmentId(null);
      return;
    }

    if (isLocalDelivery) {
      setRates([localDeliveryRate]);
      setRateId(LOCAL_DELIVERY_RATE_ID);
      setShipmentId(null);
      setRatesError('');
      clearRatesErrorToast();
      return;
    }

    const addr = shippingAddress || {};
    if (
      !addr.address ||
      !addr.city ||
      !addr.postalCode ||
      !addr.country
    )
      return;

    const key =
      `${addr.address}|${addr.city}|${
        addr.province || addr.state || ''
      }|` +
      `${addr.postalCode}|${addr.country}|${
        cartItems.length
      }`;

    if (quoteInFlightRef.current) return;
    if (lastQuoteKeyRef.current === key) return;

    lastQuoteKeyRef.current = key;
    quoteInFlightRef.current = true;

    const run = async () => {
      try {
        const payload = {
          // what backend expects
          address: addr.address,
          address2: addr.address2 || '',
          city: addr.city,
          province:
            addr.province || addr.state || '',
          postalCode: String(
            addr.postalCode || ''
          ).replace(/\s+/g, ''), // normalize "K2E 7X1" -> "K2E7X1"
          country: addr.country,

          // optional hints, safe to ignore backend-side
          items: cartItems.map((i) => ({
            qty: Number(i.qty ?? 1),
            weightOz: Number(i.weightOz ?? 0),
          })),

          name:
            guestInfo?.name ||
            userInfo?.name ||
            'Customer',
          phone: guestInfo?.phone || '',
          email:
            guestInfo?.email ||
            userInfo?.email ||
            '',
        };

        // expect { shipmentId, rates: [...] }
        const res =
          await quoteCartRates(payload).unwrap();

        const outRates = [
          ...(res?.rates || []),
        ];

        // save shipmentId for later label buy
        setShipmentId(res?.shipmentId || null);

        // sort cheapest first
        outRates.sort(
          (a, b) =>
            Number(a.rate) -
            Number(b.rate)
        );

        setRates(outRates);

        // if we don't already have a chosen rate,
        // default to cheapest
        if (!rateId && outRates.length) {
          setRateId(outRates[0].id || null);
        }

        setRatesError('');
        clearRatesErrorToast();
      } catch (e) {
        setRates([]);
        setRateId(null);
        setShipmentId(null);

        const msg =
          e?.data?.message ||
          e?.error ||
          e?.message ||
          'Failed to get rates';
        setRatesError(msg);
        showRatesErrorOnce(msg);
      } finally {
        quoteInFlightRef.current = false;
      }
    };

    // run immediately
    run();

    // and run again after slight delay
    const timer = setTimeout(run, 300);
    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fulfillmentMethod,
    isLocalDelivery,
    localDeliveryRate,
    shippingAddress.address,
    shippingAddress.address2,
    shippingAddress.city,
    shippingAddress.province,
    shippingAddress.state,
    shippingAddress.postalCode,
    shippingAddress.country,
    cartItems.length,
    guestInfo?.name,
    guestInfo?.email,
    guestInfo?.phone,
    userInfo?.name,
    userInfo?.email,
  ]);

  // ---------- Normalize items for backend ----------
  const getOrderItems = useCallback(
    () =>
      cartItems.map((i) => {
        const pricingItem = pricingLineItemMap.get(String(i.product || i._id));
        const tierPriceApplied = Boolean(
          pricingItem?.tierPriceApplied ?? i?.tierPriceApplied
        );

        return {
          name: i.displayName || i.title || i.name,
          displayName: i.displayName || i.title || i.name,
          qty: Number(i.qty ?? 0),
          image: i.image,
          product: i.product || i._id,
          price: Number(pricingItem?.price ?? i.price ?? 0),
          originalPrice: Number(
            pricingItem?.basePrice ?? i.originalPrice ?? i.price ?? 0
          ),
          tierPriceApplied,
          sku: i.sku || i.partNumber,
        };
      }),
    [cartItems, pricingLineItemMap]
  );

  const isPayPal = paymentMethod === 'PayPal';
  // If the shopper selects PayPal/Credit Card, require payment before placing the order.
  const hasTotal = Number(totals.totalRaw) > 0;
  const requiresOnlinePay =
    hasTotal &&
    isPayPal;
  const showPlaceOrder = !requiresOnlinePay;

  // ---------- Build order payload (UPDATED) ----------
  const buildShippingBlock = () => {
    if (fulfillmentMethod !== 'ship')
      return undefined;
    if (!selectedRate) return undefined;

    return {
      quoteShipmentId: shipmentId || null,
      selectedRateId: selectedRate.id || null,
      rate: {
        carrier: selectedRate.carrier,
        service: selectedRate.service,
        rate: selectedRate.rate,
        currency: selectedRate.currency,
      },
    };
  };

  const basePayload = () => ({
    orderItems: getOrderItems(),
    shippingAddress,
    fulfillmentMethod,
    pickupLocation:
      fulfillmentMethod === 'pickup'
        ? pickupLocation ||
          DEFAULT_PICKUP_LOCATION
        : null,
    itemsPrice: totals.itemsPrice,
    shippingPrice: totals.shippingPrice, // live rate
    taxPrice: totals.taxPrice,
    totalPrice: totals.totalPrice,
    paymentMethod: paymentMethod || 'PickupPay',

    // ⭐ NEW: persist shipmentId + rateId + price details
    shipping: buildShippingBlock(),
    freeDeliveryApplied: hasFreeDelivery && fulfillmentMethod === 'ship',
    shippingDiscountReason:
      hasFreeDelivery && fulfillmentMethod === 'ship'
        ? 'Free delivery account'
        : undefined,
  });

  // ---------- place order / create paid order ----------
  const placeOrderHandler = async () => {
    if (cartItems.length === 0) {
      return toast.error('Your cart is empty.');
    }
    if (
      fulfillmentMethod === 'ship' &&
      !selectedRate
    ) {
      return toast.info(
        'Please select a shipping rate.'
      );
    }
    if (requiresOnlinePay) {
      return toast.error('Please complete payment to finalize your order.');
    }

    try {
      await submitOrder(basePayload());
    } catch (err) {
      toast.error(formatError(err, 'Failed to place order'));
    }
  };

  const handlePayPalApprove = async (details) => {
    if (
      fulfillmentMethod === 'ship' &&
      !selectedRate
    ) {
      return toast.info(
        'Please select a shipping rate.'
      );
    }

    try {
      await submitOrder({
        ...basePayload(),
        paymentMethod: 'PayPal',
        paymentResult: {
          id: details?.id,
          status: details?.status,
          update_time: details?.update_time,
          email_address: details?.payer?.email_address,
        },
      });
    } catch (err) {
      toast.error(formatError(err, 'Failed to create paid order'));
    }
  };

  const paypalEnabled = paypalConfig?.enabled ?? false;
  const paypalReady = paypalEnabled && !!paypalConfig?.clientId;

  useEffect(() => {
    if (cartItems.length === 0) {
      setPricingData(null);
      setPricingError('');
      return;
    }

    const fetchPricing = async () => {
      try {
        const payload = {
          cartItems: cartItems.map((item) => ({
            productId: item.product || item._id,
            qty: item.qty,
          })),
        };
        const response = await priceCheckout(payload).unwrap();
        setPricingData(response);
        setPricingError('');
      } catch (err) {
        setPricingData(null);
        setPricingError(err?.data?.message || 'Unable to verify current pricing.');
      }
    };

    fetchPricing();
  }, [cartItems, priceCheckout]);

  return (
    <>
      <BackButton />
      <CheckoutSteps step1 step2 step3 step4 />
      <Row>
        <Col md={8}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Shipping</h2>

              {fulfillmentMethod === 'pickup' ? (
                <p>
                  <strong>Pickup:</strong>{' '}
                  {pickupLocation ||
                    DEFAULT_PICKUP_LOCATION}
                </p>
              ) : (
                <>
                  <p>
                    <strong>Address:</strong>{' '}
                    {shippingAddress.address},{' '}
                    {shippingAddress.city}
                    {shippingAddress.province ||
                    shippingAddress.state
                      ? `, ${
                          shippingAddress.province ||
                          shippingAddress.state
                        }`
                      : ''}{' '}
                    {shippingAddress.postalCode},{' '}
                    {shippingAddress.country}
                  </p>

                  {/* Rate selector */}
                  <div className="mb-2">
                    <strong>
                      Shipping Service
                    </strong>
                    {quotingRates && (
                      <div>
                        <Spinner
                          size="sm"
                          className="ms-2"
                        />{' '}
                        getting rates…
                      </div>
                    )}

                    {!quotingRates &&
                      displayRates.length === 0 && (
                        <div
                          className={
                            ratesError
                              ? 'text-danger'
                              : 'text-muted'
                          }
                        >
                          {ratesError ||
                            'No rates yet — check the address.'}
                        </div>
                      )}

                    {!quotingRates &&
                      displayRates.length > 0 && (
                        <Form.Select
                          className="mt-1"
                          value={rateId || ''}
                          onChange={(e) =>
                            setRateId(
                              e.target.value
                            )
                          }
                        >
                          {displayRates.map((r) => (
                            <option
                              key={r.id}
                              value={r.id}
                            >
                              {formatRateOption(r)}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    {fulfillmentMethod === 'ship' && isLocalDelivery && (
                      <div className="text-muted small mt-2">
                        {hasFreeDelivery ? (
                          <>Free delivery is enabled for your account.</>
                        ) : localDeliveryQualified ? (
                          <>
                            Free same-day delivery when orders are placed before{' '}
                            {LOCAL_DELIVERY_CUTOFF} on business days.
                          </>
                        ) : (
                          <>
                            Spend ${fmt(localDeliveryRemaining)} more to unlock
                            free same-day delivery in the Ottawa–Gatineau Area.
                          </>
                        )}
                      </div>
                    )}
                    {fulfillmentMethod === 'ship' && !isLocalDelivery && (
                      <div className="text-muted small mt-2">
                        Shipping rates outside the Ottawa–Gatineau Area are
                        calculated automatically at checkout.
                      </div>
                    )}
                  </div>
                </>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Payment Method</h2>
              <strong>Method:</strong>{' '}
              {paymentMethod || '—'}
              {isPayPal && totals.totalRaw > 0 && (
                <div
                  className="text-muted mt-1"
                  style={{ fontSize: 13 }}
                >
                  Pay now with PayPal to
                  complete your order.
                </div>
              )}
              {requiresOnlinePay && !paypalReady && (
                <div className="text-danger mt-2" style={{ fontSize: 13 }}>
                  Online payment is required to complete this order. Configure your PayPal client
                  ID to enable checkout.
                </div>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Order Items</h2>
              {pricingError && (
                <div className="text-warning small mb-2">{pricingError}</div>
              )}
              {cartItems.length === 0 ? (
                <Message>
                  Your cart is empty
                </Message>
              ) : (
                <ListGroup variant="flush">
                  {cartItems.map((item, index) => {
                    const pricingItem = pricingLineItemMap.get(
                      String(item.product || item._id)
                    );
                    const tierPriceApplied = Boolean(
                      pricingItem?.tierPriceApplied ?? item.tierPriceApplied
                    );
                    const effectivePrice = pricingItem
                      ? Number(pricingItem.price)
                      : tierPriceApplied
                      ? Number(item.price ?? 0)
                      : isTieredUser
                      ? Number(item.originalPrice ?? item.price)
                      : getEffectivePrice({
                          price: Number(item.originalPrice ?? item.price),
                          salePrice: item.salePrice,
                          saleStartAt: item.saleStartAt,
                          saleEndAt: item.saleEndAt,
                        });
                    return (
                      <ListGroup.Item
                        key={index}
                      >
                        <Row>
                          <Col md={1}>
                            <Image
                              src={item.image}
                              alt={item.displayName || item.title || item.name}
                              fluid
                              rounded
                            />
                          </Col>
                          <Col>
                            <Link
                              to={buildProductUrl(item.product || item._id, location)}
                              state={{ from: getCurrentRoute(location) }}
                            >
                              {item.displayName || item.title || item.name}
                            </Link>
                          </Col>
                           <Col md={4}>
                            <div>
                              <PriceWithSale
                                // crossed-out price (if any)
                                price={Number(item.originalPrice ?? item.price)}
                                // sale price when lower than original
                                salePrice={item.salePrice}
                                saleStartAt={item.saleStartAt}
                                saleEndAt={item.saleEndAt}
                                tierPrice={
                                  tierPriceApplied ? effectivePrice : null
                                }
                                showTierTag={tierPriceApplied}
                                forceBasePrice={
                                  isTieredUser && !tierPriceApplied
                                }
                              />
                            </div>
                            <div className="text-muted small">
                              {item.qty} × $
                              {fmt(effectivePrice)}{" "}
                              = $
                              {fmt(
                                Number(item.qty ?? 0) *
                                  effectivePrice
                              )}
                            </div>
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Col>

        <Col md={4}>
          <Card>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <h2>
                  {isTieredUser
                    ? 'Wholesale Order Summary'
                    : 'Order Summary'}
                </h2>
              </ListGroup.Item>

              {isTieredUser && (
                <ListGroup.Item>
                  <div className="text-muted small mb-2">
                    Built for auto parts wholesale bulk orders.
                  </div>
                  <Row>
                    <Col className="text-muted">Account Tier:</Col>
                    <Col>{tierName}</Col>
                  </Row>
                  <Row>
                    <Col className="text-muted">Order Type:</Col>
                    <Col>
                      {fulfillmentMethod === 'pickup'
                        ? 'Pickup'
                        : 'Ship'}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-muted">Line Items:</Col>
                    <Col>{orderLineCount}</Col>
                  </Row>
                  <Row>
                    <Col className="text-muted">Total Units:</Col>
                    <Col>{totalUnits}</Col>
                  </Row>
                  <Row>
                    <Col className="text-muted">
                      {fulfillmentMethod === 'pickup'
                        ? 'Pickup At:'
                        : 'Ship To:'}
                    </Col>
                    <Col>{summaryDestination}</Col>
                  </Row>
                  <Row>
                    <Col className="text-muted">Payment Method:</Col>
                    <Col>{paymentMethod || '—'}</Col>
                  </Row>
                  {fulfillmentMethod === 'ship' && (
                    <Row>
                      <Col className="text-muted">Freight Service:</Col>
                      <Col>
                        {isLocalDelivery
                          ? localDeliveryQualified
                            ? 'Free local delivery (orders over $60)'
                            : 'Local delivery (Greater Ottawa Area)'
                          : selectedRate
                          ? `${selectedRate.carrier} — ${selectedRate.service}`
                          : 'Select a shipping rate'}
                      </Col>
                    </Row>
                  )}
                </ListGroup.Item>
              )}

              <ListGroup.Item>
                <Row>
                  <Col>
                    {isTieredUser
                      ? 'Wholesale Items'
                      : 'Items'}
                    :
                  </Col>
                  <Col>
                    {isTierPricing
                      ? `${tierName} $${totals.itemsPrice}`
                      : `$${totals.itemsPrice}`}
                    {loadingPricing && (
                      <span className="text-muted small ms-2">Updating...</span>
                    )}
                  </Col>
                </Row>
              </ListGroup.Item>

              {isTieredUser && (
                <>
                  <ListGroup.Item>
                    <Row>
                      <Col className="text-muted">List Price:</Col>
                      <Col className="text-muted">
                        ${totals.originalItemsPrice}
                      </Col>
                    </Row>
                  </ListGroup.Item>

                  {Number(totals.discount) > 0 && (
                    <ListGroup.Item>
                      <Row>
                        <Col>Wholesale Savings:</Col>
                        <Col>- ${totals.discount}</Col>
                      </Row>
                    </ListGroup.Item>
                  )}
                </>
              )}

              {Number(totals.discount) > 0 && !isTieredUser && (
                <>
                  <ListGroup.Item>
                    <Row>
                      <Col className="text-muted">
                        Items before discount:
                      </Col>
                      <Col className="text-muted">
                        ${totals.originalItemsPrice}
                      </Col>
                    </Row>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <Row>
                      <Col>Discount:</Col>
                      <Col>- ${totals.discount}</Col>
                    </Row>
                  </ListGroup.Item>
                </>
              )}

              <ListGroup.Item>
                <Row>
                  <Col>Shipping:</Col>
                  <Col>
                    ${totals.shippingPrice}
                    {fulfillmentMethod === 'ship' && (
                      <div className="text-muted small">
                        {hasFreeDelivery
                          ? 'Free delivery applied to your account.'
                          : isLocalDelivery
                          ? localDeliveryQualified
                            ? 'Free local delivery applied.'
                            : 'Local delivery fee applied.'
                          : quotingRates
                          ? 'Getting live rates...'
                          : displayRates?.length
                          ? `${displayRates.length} rate${
                              displayRates.length > 1 ? 's' : ''
                            } found`
                          : 'No rates yet — check the address.'}
                        {!hasFreeDelivery && isLocalDelivery && !localDeliveryQualified && (
                          <div>
                            Spend ${fmt(localDeliveryRemaining)} more for free
                            local delivery.
                          </div>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>
              </ListGroup.Item>

              <ListGroup.Item>
                <Row>
                  <Col>Tax:</Col>
                  <Col>${totals.taxPrice}</Col>
                </Row>
              </ListGroup.Item>

              <ListGroup.Item>
                <Row>
                  <Col>Total:</Col>
                  <Col>
                    ${totals.totalPrice}
                  </Col>
                </Row>
              </ListGroup.Item>

              {(createState.error ||
                createGuestState.error) && (
                <ListGroup.Item>
                  <Message variant="danger">
                    {createState.error
                      ?.data
                      ?.message ||
                      createGuestState
                        .error
                        ?.data
                        ?.message ||
                      'Error creating order'}
                  </Message>
                </ListGroup.Item>
              )}

              {requiresOnlinePay && paypalReady ? (
                <ListGroup.Item>
                  <PayPalButtons
                    style={{
                      layout: 'vertical',
                    }}
                    createOrder={(
                      _,
                      actions
                    ) =>
                      actions.order.create({
                        purchase_units: [
                          {
                            amount: {
                              value:
                                totals.totalPrice,
                            },
                          },
                        ],
                      })
                    }
                    onApprove={async (
                      _,
                      actions
                    ) => {
                      const details =
                        await actions.order.capture();
                      await handlePayPalApprove(
                        details
                      );
                    }}
                    onError={(err) => {
                      console.error(
                        'PayPal error:',
                        err
                      );
                      toast.error(
                        'PayPal error. Please try again.'
                      );
                    }}
                  />
                </ListGroup.Item>
              ) : requiresOnlinePay && !paypalReady ? (
                <ListGroup.Item>
                  <Message variant="danger">
                    PayPal is not configured. Set <code>PAYPAL_CLIENT_ID</code> (and leave{' '}
                    <code>PAYPAL_ENABLED</code> true) to enable online payment checkout.
                  </Message>
                </ListGroup.Item>
              ) : showPlaceOrder ? (
                <ListGroup.Item>
                  <Button
                    type="button"
                    className="btn-block"
                    disabled={
                      cartItems.length ===
                        0 ||
                      (fulfillmentMethod ===
                        'ship' &&
                        !selectedRate) ||
                      createState.isLoading ||
                      createGuestState.isLoading
                    }
                    onClick={
                      placeOrderHandler
                    }
                  >
                    Place Order
                  </Button>
                  <div className="mt-2">
                <small className="text-muted">
                  See our <Link to="/shipping/policy">Shipping & Delivery Policy</Link> and{' '}
                  <Link to="/returns/policy">Return & Refund Policy</Link>.
                </small>
              </div>
                  {(createState.isLoading ||
                    createGuestState.isLoading) && (
                    <Loader />
                  )}
                </ListGroup.Item>
              ) : null}
            </ListGroup>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PlaceOrderScreen;
